import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import './Dashboard.css';
import './AdminDashboard.css';
import api, { getApiErrorMessage } from '../api';
import { formatRecent } from '../utils/dates';
import AdminModerationAI from './AdminModerationAI';
import AdminCompanyModal from './AdminCompanyModal';
import AdminPromo from './AdminPromo';
import AdminAnalytics from './AdminAnalytics';
import AdminInvites from './AdminInvites';
import AdminHelp from './AdminHelp';
import AdminNewsletter from './AdminNewsletter';
import AdminServer from './AdminServer';
import AdminBackups from './AdminBackups';
import AdminPresentations from './AdminPresentations';
import { AdminMailTab } from './AdminMailLog';

function tierUpper(t) {
    if (t === 'corporate') return 'КОРПОРАТИВНЫЙ';
    if (t === 'advanced') return 'РАСШИРЕННЫЙ';
    return 'БЕСПЛАТНЫЙ';
}

function statusUpper(s) {
    if (s === 'active') return 'АКТИВЕН';
    if (s === 'pending') return 'ОЖИДАЕТ';
    return 'ЗАБЛОКИРОВАН';
}

const TIER_NAMES = {
    free: 'Бесплатный',
    advanced: 'Расширенный',
    corporate: 'Корпоративный',
};

function formatDateRu(value) {
    return new Date(value).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function AdminDashboard() {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [inviteFormSignal, setInviteFormSignal] = useState(0);
    const [actionUser, setActionUser] = useState(null);
    const [showModal, setShowModal] = useState(false);

    const [userPolls, setUserPolls] = useState([]);
    const [showPollsModal, setShowPollsModal] = useState(false);

    const [expiryDate, setExpiryDate] = useState('');
    const [modalAction, setModalAction] = useState('approve');

    const [showTierModal, setShowTierModal] = useState(false);
    const [tierData, setTierData] = useState({ tier: 'free', expires: '' });
    const [tierSaving, setTierSaving] = useState(false);
    const [tierError, setTierError] = useState(null);
    const [adminNotice, setAdminNotice] = useState(null);

    const [showSettingsModal, setShowSettingsModal] = useState(false);
    const [advancedPrice, setAdvancedPrice] = useState('');
    const [telegramWebAuthEnabled, setTelegramWebAuthEnabled] = useState(true);


    const [openMenuUserId, setOpenMenuUserId] = useState(null);
    const [companies, setCompanies] = useState([]);
    const [companyName, setCompanyName] = useState('');
    const [companyExpiry, setCompanyExpiry] = useState('');
    const [showCreateCompanyModal, setShowCreateCompanyModal] = useState(false);
    const [selectedCompany, setSelectedCompany] = useState(null);
    const [showCompanyModal, setShowCompanyModal] = useState(false);
    // ?tab=presentations — ссылка из письма о файле, ждущем проверки
    const [activeTab, setActiveTab] = useState(() => new URLSearchParams(window.location.search).get('tab') || 'companies');
    const [userFilters, setUserFilters] = useState({
        search: '',
        status: 'all',
        tier: 'all',
        email: 'all',
    });

    const navigate = useNavigate();

    useEffect(() => {
        if (!adminNotice) return undefined;
        const timer = setTimeout(() => setAdminNotice(null), 8000);
        return () => clearTimeout(timer);
    }, [adminNotice]);

    useEffect(() => {
        fetchUsers();
        fetchCompanies();
    }, []);

    useEffect(() => {
        if (openMenuUserId == null) return undefined;
        const close = () => setOpenMenuUserId(null);
        document.addEventListener('click', close);
        return () => document.removeEventListener('click', close);
    }, [openMenuUserId]);

    const fetchUsers = async () => {
        try {
            const response = await api.get('/admin/users');
            setUsers(response.data);
        } catch (err) {
            if (err.response && err.response.status === 403) {
                setError("Доступ запрещен. Вы не администратор.");
            } else {
                setError(err.message);
            }
        } finally {
            setLoading(false);
        }
    };

    const fetchCompanies = async () => {
        try {
            const response = await api.get('/admin/companies');
            setCompanies(response.data || []);
        } catch (err) {
            console.error(err);
        }
    };

    const openActionModal = (user, action) => {
        setOpenMenuUserId(null);
        setActionUser(user);
        setModalAction(action);
        setExpiryDate('');
        setShowModal(true);
    };

    const handleStatusUpdate = async () => {
        if (!actionUser) return;

        const newStatus = modalAction === 'approve' ? 'active' : 'blocked';

        let isoDate = null;
        if (expiryDate) {
            isoDate = new Date(expiryDate).toISOString();
        }

        try {
            const response = await api.post(`/admin/users/${actionUser.id}/status`, {
                status: newStatus,
                access_expires_at: isoDate
            });

            if (response.status === 200) {
                setUsers(users.map(u => u.id === actionUser.id ? { ...u, status: newStatus, access_expires_at: isoDate } : u));
                setShowModal(false);
            } else {
                alert("Ошибка обновления статуса");
            }
        } catch (err) {
            console.error(err);
            alert("Ошибка сети");
        }
    };

    const openTierModal = (user) => {
        setOpenMenuUserId(null);
        setActionUser(user);
        setTierError(null);
        // Пока тариф выдаёт компания, здесь меняется личный тариф (вернётся после выхода из компании)
        const viaCompany = Boolean(user.company_name);
        const tier = viaCompany ? (user.personal_tier || 'free') : (user.subscription_tier || 'free');
        const expires = viaCompany ? user.personal_expires_at : user.subscription_expires_at;
        setTierData({
            tier,
            expires: expires ? new Date(expires).toISOString().split('T')[0] : ''
        });
        setShowTierModal(true);
    };

    const closeTierModal = () => {
        if (tierSaving) return;
        setShowTierModal(false);
    };

    const handleTierSubmit = async () => {
        if (!actionUser || tierSaving) return;

        let isoDate = null;
        if (tierData.expires) {
            isoDate = new Date(tierData.expires).toISOString();
        }

        setTierSaving(true);
        setTierError(null);
        try {
            const response = await api.post(`/admin/users/${actionUser.id}/subscription`, {
                subscription_tier: tierData.tier,
                subscription_expires_at: isoDate
            });
            const updatedUser = response.data;
            setUsers((prev) => prev.map(u => u.id === actionUser.id ? {
                ...u,
                subscription_tier: updatedUser.subscription_tier,
                subscription_expires_at: updatedUser.subscription_expires_at,
                personal_tier: updatedUser.personal_tier,
                personal_expires_at: updatedUser.personal_expires_at,
            } : u));
            setShowTierModal(false);
            const who = actionUser.email || actionUser.first_name || actionUser.username || `ID ${actionUser.id}`;
            if (updatedUser.company_name) {
                const personalName = TIER_NAMES[updatedUser.personal_tier] || updatedUser.personal_tier;
                const personalUntil = updatedUser.personal_expires_at ? `до ${formatDateRu(updatedUser.personal_expires_at)}` : 'бессрочно';
                setAdminNotice({ type: 'success', text: `Личный тариф пользователя ${who} изменён: ${personalName}, ${personalUntil}. Сейчас действует корпоративный тариф от компании «${updatedUser.company_name}» — личный вступит в силу после выхода из неё.` });
                return;
            }
            const tierName = TIER_NAMES[updatedUser.subscription_tier] || updatedUser.subscription_tier;
            const until = updatedUser.subscription_expires_at
                ? `до ${formatDateRu(updatedUser.subscription_expires_at)}`
                : 'бессрочно';
            setAdminNotice({ type: 'success', text: `Тариф пользователя ${who} изменён: ${tierName}, ${until}.` });
        } catch (err) {
            console.error(err);
            setTierError(getApiErrorMessage(err, 'Не удалось изменить тариф. Проверьте соединение и попробуйте ещё раз.'));
        } finally {
            setTierSaving(false);
        }
    };

    const deleteUser = async (userId) => {
        setOpenMenuUserId(null);
        if (!window.confirm("Удалить пользователя? Будут удалены аккаунт, все его опросы с ответами и история платежей в СВОД. Действие необратимо.\n\nЕсли нужно только закрыть доступ — используйте «Заблокировать».")) return;

        try {
            const response = await api.delete(`/admin/users/${userId}`);
            if (response.status === 200) {
                setUsers(users.filter(u => u.id !== userId));
            } else {
                alert("Ошибка при удалении");
            }
        } catch (err) {
            console.error(err);
            setAdminNotice({ type: 'error', text: `Пользователь не удалён: ${getApiErrorMessage(err, 'ошибка сервера')}` });
        }
    };

    const generateInvite = () => {
        setActiveTab('invites');
        setInviteFormSignal((n) => n + 1);
    };

    const handleGenerateResetLink = async (user) => {
        setOpenMenuUserId(null);
        try {
            const response = await api.post(`/admin/users/${user.id}/reset-password-link`);
            if (response.status === 200) {
                prompt('Ссылка для сброса пароля сгенерирована. Скопируйте её и отправьте пользователю:', response.data.link);
            } else {
                alert(`Ошибка: Не удалось сгенерировать ссылку`);
            }
        } catch (err) {
            console.error(err);
            const detail = getApiErrorMessage(err, "Ошибка сети при генерации ссылки");
            alert(`Ошибка: ${detail}`);
        }
    };

    const openSettingsModal = async () => {
        try {
            const [settingsRes, authConfigRes] = await Promise.all([
                api.get('/settings'),
                api.get('/auth/config'),
            ]);
            const data = Array.isArray(settingsRes.data) ? settingsRes.data : [];
            const setting = data.find(s => s.key === 'tariff_advanced_price');
            setAdvancedPrice(setting ? setting.value : '500');
            setTelegramWebAuthEnabled(authConfigRes.data.telegram_web_auth_enabled !== false);
            setShowSettingsModal(true);
        } catch (err) {
            console.error(err);
            alert("Ошибка загрузки настроек");
        }
    };

    const handleSettingsSubmit = async () => {
        try {
            await Promise.all([
                api.patch('/admin/settings/tariff_advanced_price', { value: advancedPrice }),
                api.patch('/admin/settings/telegram_web_auth_enabled', {
                    value: telegramWebAuthEnabled ? 'true' : 'false',
                }),
            ]);
            setShowSettingsModal(false);
        } catch (err) {
            console.error(err);
            alert("Ошибка сохранения настроек");
        }
    };

    const openCreateCompanyModal = () => {
        setCompanyName('');
        setCompanyExpiry('');
        setShowCreateCompanyModal(true);
    };

    const handleCreateCompany = async () => {
        if (!companyName.trim()) {
            alert('Введите название компании');
            return;
        }
        try {
            const payload = {
                name: companyName.trim(),
                status: 'active',
                subscription_expires_at: companyExpiry ? new Date(companyExpiry).toISOString() : null,
            };
            const created = await api.post('/admin/companies', payload);
            setShowCreateCompanyModal(false);
            await fetchCompanies();
            // Сразу открываем компанию, чтобы добавить участников
            openCompanyModal(created.data.id);
        } catch (err) {
            console.error(err);
            alert(getApiErrorMessage(err, 'Ошибка создания компании'));
        }
    };

    const openCompanyModal = (companyId) => {
        setSelectedCompany(companyId);
        setShowCompanyModal(true);
    };

    const [deletingPollId, setDeletingPollId] = useState(null);

    const deleteUserPoll = async (poll) => {
        const owner = actionUser?.email || actionUser?.first_name || `ID ${actionUser?.id}`;
        const confirmed = window.confirm(
            `Удалить опрос «${poll.title}» пользователя ${owner}?\n\n` +
            'Будут удалены все вопросы и ответы участников. Действие нельзя отменить.'
        );
        if (!confirmed) return;
        setDeletingPollId(poll.id);
        try {
            await api.delete(`/admin/polls/${poll.id}`);
            setUserPolls((prev) => prev.filter((p) => p.id !== poll.id));
            setUsers((prev) => prev.map((u) => (
                u.id === actionUser.id ? { ...u, poll_count: Math.max(0, (u.poll_count || 1) - 1) } : u
            )));
            setAdminNotice({ type: 'success', text: `Опрос «${poll.title}» пользователя ${owner} удалён.` });
        } catch (err) {
            console.error(err);
            alert(`Опрос не удалён: ${getApiErrorMessage(err, 'ошибка сервера')}`);
        } finally {
            setDeletingPollId(null);
        }
    };

    const fetchUserPolls = async (user) => {
        setOpenMenuUserId(null);
        try {
            const response = await api.get(`/polls/teacher/${user.id}`);
            if (response.status === 200) {
                setUserPolls(response.data);
                setActionUser(user);
                setShowPollsModal(true);
            } else {
                alert("Не удалось загрузить опросы");
            }
        } catch (err) {
            console.error(err);
            const detail = getApiErrorMessage(err, "Не удалось загрузить опросы");
            alert(detail);
        }
    };

    const filteredUsers = useMemo(() => {
        const searchValue = userFilters.search.trim().toLowerCase();

        return users.filter((user) => {
            if (userFilters.status !== 'all' && user.status !== userFilters.status) {
                return false;
            }

            const subscriptionTier = user.subscription_tier || 'free';
            if (userFilters.tier !== 'all' && subscriptionTier !== userFilters.tier) {
                return false;
            }

            const hasEmail = Boolean(user.email);
            if (userFilters.email === 'yes' && !hasEmail) {
                return false;
            }
            if (userFilters.email === 'no' && hasEmail) {
                return false;
            }

            if (!searchValue) {
                return true;
            }

            const searchFields = [
                String(user.id),
                user.first_name || '',
                user.username || '',
                user.email || '',
                user.telegram_id != null ? String(user.telegram_id) : '',
            ];

            return searchFields.some((field) => field.toLowerCase().includes(searchValue));
        });
    }, [users, userFilters]);

    const isUsersFilterActive = userFilters.search.trim() !== ''
        || userFilters.status !== 'all'
        || userFilters.tier !== 'all'
        || userFilters.email !== 'all';

    if (loading) {
        return (
            <div className="admin-page">
                <div className="admin-loading">Загрузка…</div>
            </div>
        );
    }
    if (error) {
        return (
            <div className="admin-page">
                <div className="admin-inner">
                    <div className="admin-error-panel">{error}</div>
                </div>
            </div>
        );
    }

    return (
        <div className="admin-page">
            <header className="admin-top">
                <div className="admin-top-inner">
                    <h1 className="admin-top-title">
                        <svg className="admin-shield-icon" width="26" height="26" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                            <path d="M12 2L4 5v6.09c0 5.05 3.41 9.76 8 10.91 4.59-1.15 8-5.86 8-10.91V5l-8-3zm-1 14.09l-3.5-3.5 1.41-1.41L11 13.17l4.59-4.59L17 10l-6 6.09z" opacity="0.95" />
                        </svg>
                        Панель администратора
                    </h1>
                    <div style={{ display: 'flex', gap: '1rem' }}>
                        <button type="button" className="dashboard-header-link" onClick={() => setActiveTab('server')}>
                            Инфо о сервере
                        </button>
                        <button type="button" className="dashboard-header-link" onClick={openSettingsModal}>
                            Настройки
                        </button>
                        <button type="button" className="dashboard-header-link" onClick={() => navigate('/dashboard')}>
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden style={{ marginRight: '4px' }}>
                                <path d="M3 10.5L12 3l9 7.5V20a1 1 0 01-1 1h-5v-7H9v7H4a1 1 0 01-1-1v-9.5z" fill="currentColor" />
                            </svg>
                            В кабинет
                        </button>
                    </div>
                </div>
            </header>

            <div className="admin-inner">
                {adminNotice && (
                    <div className={`admin-notice admin-notice--${adminNotice.type} admin-notice--page`} role="status">
                        <span>{adminNotice.text}</span>
                        <button type="button" className="admin-notice__close" onClick={() => setAdminNotice(null)} aria-label="Скрыть">✕</button>
                    </div>
                )}
                <div className="admin-invite-row">
                    <div className="admin-actions-row">
                        <button type="button" className="admin-invite-btn" onClick={generateInvite}>
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
                                <path d="M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                                <circle cx="9" cy="7" r="4" stroke="currentColor" strokeWidth="2" />
                                <path d="M19 8v6M22 11h-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                            </svg>
                            Пригласить организатора
                        </button>
                        <button type="button" className="admin-invite-btn" onClick={openCreateCompanyModal}>
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
                                <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                            </svg>
                            Создать компанию
                        </button>
                    </div>
                </div>

                <div className="admin-tabs" role="tablist" aria-label="Разделы админки">
                    <button
                        type="button"
                        role="tab"
                        aria-selected={activeTab === 'companies'}
                        className={`admin-tab-btn ${activeTab === 'companies' ? 'admin-tab-btn--active' : ''}`}
                        onClick={() => setActiveTab('companies')}
                    >
                        Компании ({companies.length})
                    </button>
                    <button
                        type="button"
                        role="tab"
                        aria-selected={activeTab === 'users'}
                        className={`admin-tab-btn ${activeTab === 'users' ? 'admin-tab-btn--active' : ''}`}
                        onClick={() => setActiveTab('users')}
                    >
                        Пользователи ({users.length})
                    </button>
                    <button
                        type="button"
                        role="tab"
                        aria-selected={activeTab === 'moderation-ai'}
                        className={`admin-tab-btn ${activeTab === 'moderation-ai' ? 'admin-tab-btn--active' : ''}`}
                        onClick={() => setActiveTab('moderation-ai')}
                    >
                        ИИ-модерация
                    </button>
                    <button
                        type="button"
                        role="tab"
                        aria-selected={activeTab === 'promo'}
                        className={`admin-tab-btn ${activeTab === 'promo' ? 'admin-tab-btn--active' : ''}`}
                        onClick={() => setActiveTab('promo')}
                    >
                        Промокоды
                    </button>
                    <button
                        type="button"
                        role="tab"
                        aria-selected={activeTab === 'analytics'}
                        className={`admin-tab-btn ${activeTab === 'analytics' ? 'admin-tab-btn--active' : ''}`}
                        onClick={() => setActiveTab('analytics')}
                    >
                        Аналитика
                    </button>
                    <button
                        type="button"
                        role="tab"
                        aria-selected={activeTab === 'invites'}
                        className={`admin-tab-btn ${activeTab === 'invites' ? 'admin-tab-btn--active' : ''}`}
                        onClick={() => setActiveTab('invites')}
                    >
                        Приглашения
                    </button>
                    <button
                        type="button"
                        role="tab"
                        aria-selected={activeTab === 'newsletter'}
                        className={`admin-tab-btn ${activeTab === 'newsletter' ? 'admin-tab-btn--active' : ''}`}
                        onClick={() => setActiveTab('newsletter')}
                    >
                        Рассылки
                    </button>
                    <button
                        type="button"
                        role="tab"
                        aria-selected={activeTab === 'presentations'}
                        className={`admin-tab-btn ${activeTab === 'presentations' ? 'admin-tab-btn--active' : ''}`}
                        onClick={() => setActiveTab('presentations')}
                    >
                        Презентации
                    </button>
                    <button
                        type="button"
                        role="tab"
                        aria-selected={activeTab === 'mail'}
                        className={`admin-tab-btn ${activeTab === 'mail' ? 'admin-tab-btn--active' : ''}`}
                        onClick={() => setActiveTab('mail')}
                    >
                        Почта
                    </button>
                    <button
                        type="button"
                        role="tab"
                        aria-selected={activeTab === 'server'}
                        className={`admin-tab-btn ${activeTab === 'server' ? 'admin-tab-btn--active' : ''}`}
                        onClick={() => setActiveTab('server')}
                    >
                        Сервер
                    </button>
                    <button
                        type="button"
                        role="tab"
                        aria-selected={activeTab === 'backups'}
                        className={`admin-tab-btn ${activeTab === 'backups' ? 'admin-tab-btn--active' : ''}`}
                        onClick={() => setActiveTab('backups')}
                    >
                        Резервные копии
                    </button>
                    <button
                        type="button"
                        role="tab"
                        aria-selected={activeTab === 'help'}
                        className={`admin-tab-btn ${activeTab === 'help' ? 'admin-tab-btn--active' : ''}`}
                        onClick={() => setActiveTab('help')}
                    >
                        Справка
                    </button>
                </div>

                {activeTab === 'moderation-ai' && <AdminModerationAI />}
                {activeTab === 'promo' && <AdminPromo />}
                {activeTab === 'analytics' && <AdminAnalytics />}
                {activeTab === 'invites' && <AdminInvites openFormSignal={inviteFormSignal} />}
                {activeTab === 'newsletter' && <AdminNewsletter onOpenTab={setActiveTab} />}
                {activeTab === 'mail' && <AdminMailTab />}
                {activeTab === 'presentations' && <AdminPresentations />}
                {activeTab === 'server' && <AdminServer />}
                {activeTab === 'backups' && <AdminBackups />}
                {activeTab === 'help' && <AdminHelp onOpenTab={setActiveTab} />}

                {activeTab === 'companies' && (
                    <section className="admin-main-card">
                        <h2 className="admin-main-card__title">Компании ({companies.length})</h2>
                        <div className="company-intro">
                            <b>Компания выдаёт тариф «Корпоративный» сразу группе людей</b> — например, всем преподавателям вуза или сотрудникам клиента.
                            <ul>
                                <li>Пока компания активна и срок не истёк, все её участники работают на корпоративном тарифе.</li>
                                <li>Личный тариф участника (оплаченный или выданный вручную) сохраняется и вернётся после выхода из компании или окончания срока.</li>
                                <li>Нажмите на компанию, чтобы изменить срок или статус, добавить или убрать участников.</li>
                            </ul>
                        </div>
                        <div className="admin-table-scroll">
                            <table className="admin-table company-table">
                                <thead>
                                    <tr>
                                        <th>ID</th>
                                        <th>Название</th>
                                        <th>Статус</th>
                                        <th>Участников</th>
                                        <th>Подписка до</th>
                                        <th>Действия</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {companies.map((company) => (
                                        <tr key={company.id}>
                                            <td>{company.id}</td>
                                            <td>
                                                <button type="button" className="company-name-btn" onClick={() => openCompanyModal(company.id)}>
                                                    {company.name}
                                                </button>
                                            </td>
                                            <td>
                                                {(() => {
                                                    const expired = company.subscription_expires_at && new Date(company.subscription_expires_at) < new Date();
                                                    const active = company.status === 'active' && !expired;
                                                    return (
                                                        <span className={`status-mock status-mock--${active ? 'active' : 'blocked'}`}>
                                                            {company.status !== 'active' ? 'ПРИОСТАНОВЛЕНА' : expired ? 'СРОК ИСТЁК' : 'АКТИВНА'}
                                                        </span>
                                                    );
                                                })()}
                                            </td>
                                            <td>{company.members_count || 0}</td>
                                            <td>
                                                {company.subscription_expires_at
                                                    ? new Date(company.subscription_expires_at).toLocaleDateString()
                                                    : 'Бессрочно'}
                                            </td>
                                            <td className="actions-cell">
                                                <button type="button" className="btn btn--secondary company-action-btn" onClick={() => openCompanyModal(company.id)}>
                                                    Открыть
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </section>
                )}

                {activeTab === 'users' && (
                    <section className="admin-main-card">
                        <h2 className="admin-main-card__title">
                            Пользователи ({filteredUsers.length}{isUsersFilterActive ? ` из ${users.length}` : ''})
                        </h2>
                        <div className="admin-users-filters">
                            <input
                                type="text"
                                className="admin-users-filters__search"
                                placeholder="Поиск: ID, имя, username, email, Telegram ID"
                                value={userFilters.search}
                                onChange={(e) => setUserFilters((prev) => ({ ...prev, search: e.target.value }))}
                            />
                            <select
                                className="admin-users-filters__select"
                                value={userFilters.status}
                                onChange={(e) => setUserFilters((prev) => ({ ...prev, status: e.target.value }))}
                            >
                                <option value="all">Все статусы</option>
                                <option value="active">Активен</option>
                                <option value="pending">Ожидает</option>
                                <option value="blocked">Заблокирован</option>
                            </select>
                            <select
                                className="admin-users-filters__select"
                                value={userFilters.tier}
                                onChange={(e) => setUserFilters((prev) => ({ ...prev, tier: e.target.value }))}
                            >
                                <option value="all">Все тарифы</option>
                                <option value="free">Бесплатный</option>
                                <option value="advanced">Расширенный</option>
                                <option value="corporate">Корпоративный</option>
                            </select>
                            <select
                                className="admin-users-filters__select"
                                value={userFilters.email}
                                onChange={(e) => setUserFilters((prev) => ({ ...prev, email: e.target.value }))}
                            >
                                <option value="all">Email: все</option>
                                <option value="yes">Email: есть</option>
                                <option value="no">Email: нет</option>
                            </select>
                            <button
                                type="button"
                                className="btn btn--secondary admin-users-filters__reset"
                                onClick={() => setUserFilters({ search: '', status: 'all', tier: 'all', email: 'all' })}
                                disabled={!isUsersFilterActive}
                            >
                                Сбросить
                            </button>
                        </div>
                        <div className="admin-table-scroll">
                            <table className="admin-table">
                                <thead>
                                    <tr>
                                        <th>ID</th>
                                        <th>Имя / Username</th>
                                        <th>Email</th>
                                        <th>Опросы</th>
                                        <th>Согласия</th>
                                        <th>Тариф</th>
                                        <th>Статус</th>
                                        <th>Активность</th>
                                        <th>Тариф до</th>
                                        <th>Действия</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredUsers.map(user => (
                                        <tr key={user.id}>
                                            <td title={user.telegram_id != null ? `Telegram ID: ${String(user.telegram_id)}` : undefined}>
                                                {user.id}
                                            </td>
                                            <td>
                                                <div className="admin-user-name">
                                                    {user.first_name}
                                                    {user.username ? ` (@${user.username})` : ''}
                                                    {user.role === 'admin' && (
                                                        <span className="admin-role-tag" title="Администратор">admin</span>
                                                    )}
                                                </div>
                                                <div className="date">{new Date(user.created_at).toLocaleDateString()}</div>
                                            </td>
                                            <td>
                                                {user.email ? (
                                                    <>
                                                        <span className="admin-email-yes">{user.email}</span>
                                                        {user.is_verified && <span className="verified-badge" title="Верифицирован">✓</span>}
                                                    </>
                                                ) : (
                                                    <span className="admin-email-no">Нет</span>
                                                )}
                                            </td>
                                            <td className="poll-count-cell">
                                                <button
                                                    type="button"
                                                    className="pollcount-badge"
                                                    onClick={() => fetchUserPolls(user)}
                                                >
                                                    {user.poll_count || 0}
                                                </button>
                                            </td>
                                            <td className="consent-cell">
                                                <span
                                                    className={user.agreed_to_terms_at ? 'consent-yes' : 'consent-no'}
                                                    title={user.agreed_to_terms_at ? `Оферта принята: ${new Date(user.agreed_to_terms_at).toLocaleString()}` : 'Оферта не принята'}
                                                >
                                                    Оферта {user.agreed_to_terms_at ? '✓' : '—'}
                                                </span>
                                                <span
                                                    className={user.agreed_to_newsletter ? 'consent-yes' : 'consent-no'}
                                                    title={user.agreed_to_newsletter
                                                        ? (user.newsletter_consent_at ? `Согласие на рассылку: ${new Date(user.newsletter_consent_at).toLocaleString()}` : 'Согласие на рассылку дано при регистрации (до учёта даты)')
                                                        : (user.newsletter_revoked_at ? `Отписался от рассылки: ${new Date(user.newsletter_revoked_at).toLocaleString()}` : 'Согласия на рассылку нет')}
                                                >
                                                    Рассылка {user.agreed_to_newsletter ? '✓' : (user.newsletter_revoked_at ? '✕' : '—')}
                                                </span>
                                            </td>
                                            <td>
                                                <span className="tier-mock">{tierUpper(user.subscription_tier)}</span>
                                                {user.company_name && (
                                                    <span className="tier-company-note" title="Тариф выдаёт компания; личный тариф вернётся после выхода из неё">
                                                        через компанию «{user.company_name}»
                                                    </span>
                                                )}
                                            </td>
                                            <td>
                                                <span className={`status-mock status-mock--${user.status}`}>{statusUpper(user.status)}</span>
                                            </td>
                                            <td className="activity-cell">
                                                {user.last_activity_at ? (
                                                    <span
                                                        className={user.last_seen_at ? 'activity-seen' : 'activity-approx'}
                                                        title={user.last_seen_at
                                                            ? `Последний визит в кабинет: ${new Date(user.last_seen_at).toLocaleString()}`
                                                            : 'Визитов после начала учёта не было — показана дата последнего созданного опроса или сессии вопросов'}
                                                    >
                                                        {formatRecent(user.last_activity_at)}
                                                    </span>
                                                ) : (
                                                    <span className="activity-none" title="Пользователь ещё ничего не делал в кабинете">—</span>
                                                )}
                                            </td>
                                            <td className="expiry-cell">
                                                {user.subscription_expires_at
                                                    ? new Date(user.subscription_expires_at).toLocaleDateString()
                                                    : <span style={{ color: 'var(--text-muted, #888)', fontSize: '0.85em' }}>—</span>}
                                            </td>
                                            <td className="actions-cell">
                                                <div className="admin-row-menu">
                                                    <button
                                                        type="button"
                                                        className="admin-row-menu__trigger"
                                                        aria-expanded={openMenuUserId === user.id}
                                                        aria-label="Меню действий"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setOpenMenuUserId(openMenuUserId === user.id ? null : user.id);
                                                        }}
                                                    />
                                                    {openMenuUserId === user.id && (
                                                        <div
                                                            className="admin-row-menu__panel"
                                                            role="menu"
                                                            onClick={(e) => e.stopPropagation()}
                                                        >
                                                            <button type="button" className="admin-menu-item" role="menuitem" onClick={() => fetchUserPolls(user)}>
                                                                Опросы
                                                            </button>
                                                            {user.status !== 'blocked' && (
                                                                <>
                                                                    <button type="button" className="admin-menu-item" role="menuitem" onClick={() => openTierModal(user)}>
                                                                        Тариф
                                                                    </button>
                                                                    <button type="button" className="admin-menu-item" role="menuitem" onClick={() => handleGenerateResetLink(user)}>
                                                                        Сброс пароля
                                                                    </button>
                                                                    <button type="button" className="admin-menu-item" role="menuitem" onClick={() => openActionModal(user, 'block')}>
                                                                        Заблокировать
                                                                    </button>
                                                                </>
                                                            )}
                                                            {user.status === 'blocked' && (
                                                                <button type="button" className="admin-menu-item" role="menuitem" onClick={() => openActionModal(user, 'approve')}>
                                                                    Разблокировать
                                                                </button>
                                                            )}
                                                            <button type="button" className="admin-menu-item admin-menu-item--danger" role="menuitem" onClick={() => deleteUser(user.id)}>
                                                                Удалить
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                    {filteredUsers.length === 0 && (
                                        <tr>
                                            <td colSpan={9} className="admin-users-empty">
                                                По выбранным фильтрам пользователи не найдены
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </section>
                )}
            </div>

            {showModal && (
                <div className="admin-modal-overlay" role="presentation" onClick={() => setShowModal(false)}>
                    <div className="admin-modal" role="dialog" onClick={(e) => e.stopPropagation()}>
                        <div className="admin-modal__body">
                            <h3>{modalAction === 'approve' ? 'Разблокировка' : 'Блокировка'}</h3>
                            <p>Пользователь: {actionUser?.first_name || actionUser?.email}</p>
                            <p className="admin-modal-hint">
                                {modalAction === 'approve'
                                    ? 'Пользователь снова сможет создавать и запускать опросы, а его опросы — принимать ответы.'
                                    : 'Пользователь не сможет создавать и запускать опросы, его опросы перестанут принимать ответы. Данные сохраняются, блокировку можно снять.'}
                            </p>
                        </div>
                        <div className="admin-modal__footer">
                            <button type="button" className="btn btn--secondary" onClick={() => setShowModal(false)}>Отмена</button>
                            <button
                                type="button"
                                className={modalAction === 'approve' ? 'btn btn--success' : 'btn btn--danger'}
                                onClick={handleStatusUpdate}
                            >
                                {modalAction === 'approve' ? 'Разблокировать' : 'Заблокировать'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showPollsModal && actionUser && (
                <div className="admin-modal-overlay" role="presentation" onClick={() => setShowPollsModal(false)}>
                    <div className="admin-modal admin-modal--wide" role="dialog" onClick={(e) => e.stopPropagation()}>
                        <div className="admin-modal__body">
                            <h3>Опросы: {actionUser.first_name || actionUser.email || actionUser.username}</h3>
                            {userPolls.length > 0 && (
                                <p className="polls-summary">
                                    Всего опросов: <b>{userPolls.length}</b>
                                    {' · '}респондентов: <b>{userPolls.reduce((sum, p) => sum + (p.response_count || 0), 0).toLocaleString('ru-RU')}</b>
                                    {' · '}ответов: <b>{userPolls.reduce((sum, p) => sum + (p.answer_count || 0), 0).toLocaleString('ru-RU')}</b>
                                </p>
                            )}
                            <div className="polls-list-scroll">
                                {userPolls.length === 0 ? (
                                    <p className="admin-modal-empty">У пользователя нет опросов.</p>
                                ) : (
                                    <ul>
                                        {userPolls.map(poll => {
                                            const pollRef = poll.public_id || String(poll.id);
                                            const link = `${window.location.origin}/poll/${pollRef}`;

                                            return (
                                                <li key={poll.id} className="poll-item">
                                                    <div className="poll-item__head">
                                                        <div className="poll-info">
                                                            <strong>{poll.title}</strong>
                                                            <span className="poll-date">
                                                                {new Date(poll.created_at).toLocaleDateString()}
                                                                {' · '}вопросов: {poll.questions?.length || 0}
                                                                {' · '}{poll.archived_at ? 'в архиве' : poll.is_active ? 'активен' : 'не активен'}
                                                            </span>
                                                            <span className="poll-activity">
                                                                Респондентов: <b>{(poll.response_count || 0).toLocaleString('ru-RU')}</b>
                                                                {' · '}ответов: <b>{(poll.answer_count || 0).toLocaleString('ru-RU')}</b>
                                                                {poll.last_response_at && <>{' · '}последний ответ {formatRecent(poll.last_response_at)}</>}
                                                            </span>
                                                        </div>
                                                        <button
                                                            type="button"
                                                            className="btn btn--danger-outline btn--sm"
                                                            onClick={() => deleteUserPoll(poll)}
                                                            disabled={deletingPollId !== null}
                                                        >
                                                            {deletingPollId === poll.id ? 'Удаление…' : 'Удалить'}
                                                        </button>
                                                    </div>
                                                    <div className="poll-link">
                                                        <input type="text" readOnly value={link} onClick={e => e.target.select()} />
                                                    </div>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                )}
                            </div>
                        </div>
                        <div className="admin-modal__footer">
                            <button type="button" className="btn btn--secondary" onClick={() => setShowPollsModal(false)}>Закрыть</button>
                        </div>
                    </div>
                </div>
            )}

            {showTierModal && (
                <div className="admin-modal-overlay" role="presentation" onClick={closeTierModal}>
                    <div className="admin-modal" role="dialog" onClick={(e) => e.stopPropagation()}>
                        <div className="admin-modal__body">
                            <h3>Управление тарифом</h3>
                            <p>Пользователь: {actionUser?.first_name || actionUser?.email || actionUser?.username}</p>
                            {actionUser?.company_name && (
                                <div className="admin-notice" role="note">
                                    Сейчас тариф «Корпоративный» выдаёт компания «{actionUser.company_name}». Здесь вы меняете <b>личный тариф</b> — он вступит в силу, когда пользователь выйдет из компании или её срок закончится.
                                </div>
                            )}

                            <div className="form-group">
                                <label htmlFor="admin-tier-select">Тариф</label>
                                <select
                                    id="admin-tier-select"
                                    value={tierData.tier}
                                    onChange={(e) => setTierData({ ...tierData, tier: e.target.value })}
                                >
                                    <option value="free">Бесплатный</option>
                                    <option value="advanced">Расширенный</option>
                                    <option value="corporate">Корпоративный</option>
                                </select>
                            </div>

                            <div className="form-group">
                                <label htmlFor="admin-tier-expires">Действует до (пусто — бессрочно)</label>
                                <input
                                    id="admin-tier-expires"
                                    type="date"
                                    value={tierData.expires}
                                    onChange={(e) => setTierData({ ...tierData, expires: e.target.value })}
                                />
                            </div>
                            {tierError && (
                                <div className="admin-notice admin-notice--error" role="alert">
                                    Тариф не изменён: {tierError}
                                </div>
                            )}
                        </div>
                        <div className="admin-modal__footer">
                            <button type="button" className="btn btn--secondary" onClick={closeTierModal} disabled={tierSaving}>Отмена</button>
                            <button type="button" className="btn btn--success" onClick={handleTierSubmit} disabled={tierSaving}>
                                {tierSaving ? 'Сохранение…' : 'Сохранить'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showSettingsModal && (
                <div className="admin-modal-overlay" role="presentation" onClick={() => setShowSettingsModal(false)}>
                    <div className="admin-modal" role="dialog" onClick={(e) => e.stopPropagation()}>
                        <div className="admin-modal__body">
                            <h3>Настройки системы</h3>
                            <div className="form-group" style={{ marginTop: '1rem' }}>
                                <label htmlFor="admin-adv-price">Стоимость Расширенного тарифа (руб.)</label>
                                <input
                                    id="admin-adv-price"
                                    type="number"
                                    value={advancedPrice}
                                    onChange={(e) => setAdvancedPrice(e.target.value)}
                                    className="form-input"
                                />
                            </div>
                            <div className="form-group" style={{ marginTop: '1rem' }}>
                                <label className="login-checkbox-row" style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', cursor: 'pointer' }}>
                                    <input
                                        type="checkbox"
                                        checked={telegramWebAuthEnabled}
                                        onChange={(e) => setTelegramWebAuthEnabled(e.target.checked)}
                                    />
                                    <span>
                                        Разрешить функционал организатора через Teacher Bot и вход через Telegram в веб-интерфейсе
                                    </span>
                                </label>
                                <p style={{ marginTop: '0.5rem', fontSize: '0.875rem', color: 'var(--color-text-muted)' }}>
                                    При отключении Teacher Bot недоступен для организаторов (создание и редактирование опросов), вкладка Telegram скрывается на странице входа. Голосование респондентов в Student Bot не затрагивается.
                                </p>
                            </div>
                        </div>
                        <div className="admin-modal__footer">
                            <button type="button" className="btn btn--secondary" onClick={() => setShowSettingsModal(false)}>Отмена</button>
                            <button type="button" className="btn btn--success" onClick={handleSettingsSubmit}>Сохранить</button>
                        </div>
                    </div>
                </div>
            )}

            {showCreateCompanyModal && (
                <div className="admin-modal-overlay" role="presentation" onClick={() => setShowCreateCompanyModal(false)}>
                    <div className="admin-modal" role="dialog" onClick={(e) => e.stopPropagation()}>
                        <div className="admin-modal__body">
                            <h3>Создать компанию</h3>
                            <div className="form-group">
                                <label htmlFor="company-name-input">Название</label>
                                <input
                                    id="company-name-input"
                                    type="text"
                                    value={companyName}
                                    onChange={(e) => setCompanyName(e.target.value)}
                                />
                            </div>
                            <div className="form-group">
                                <label htmlFor="company-expires-input">Подписка до (пусто — бессрочно)</label>
                                <input
                                    id="company-expires-input"
                                    type="date"
                                    value={companyExpiry}
                                    onChange={(e) => setCompanyExpiry(e.target.value)}
                                />
                            </div>
                        </div>
                        <div className="admin-modal__footer">
                            <button type="button" className="btn btn--secondary" onClick={() => setShowCreateCompanyModal(false)}>Отмена</button>
                            <button type="button" className="btn btn--success" onClick={handleCreateCompany}>Создать</button>
                        </div>
                    </div>
                </div>
            )}

            {showCompanyModal && selectedCompany && (
                <AdminCompanyModal
                    companyId={selectedCompany}
                    users={users}
                    onClose={() => setShowCompanyModal(false)}
                    onChanged={() => { fetchCompanies(); fetchUsers(); }}
                />
            )}
        </div>
    );
}

export default AdminDashboard;
